import { Col, Form, Input, Row, Select, Switch } from "antd";
import * as React from "react";
import { useForm } from "antd/lib/form/Form";
import CopyToClipboardButton from "../../common/components/CopyToClipboardButton";
import { ProjectsStore } from "../../common/stores";
import { Utils } from "../../common/misc/Utils";
import security from "../../common/services/SecurityService";

const FormItem = Form.Item;

type AddProjectFormProps = {
    onAddProject: (
        name: string,
        owner: string | undefined,
        enrichmentEnabled: boolean,
    ) => void;
    onSubmitCallback?: () => void;
    store: ProjectsStore;
};

interface Hash {
    [details: string]: string & string[] & boolean;
}

type ProjectOwnerOption = {
    value: string;
    label: string;
    customFilterValue: string;
};

export const AddProjectForm: React.FC<AddProjectFormProps> = ({
    store,
    onAddProject,
    onSubmitCallback,
}) => {
    const [form] = useForm();
    const isEditing = !!store.editableProject;

    const handleSubmit = async () => {
        const values = form.getFieldsValue(true) as Hash;

        await onAddProject(
            values.name,
            values.projectOwner as string | undefined,
            !!values.enrichmentEnabled,
        );

        if (onSubmitCallback) {
            onSubmitCallback();
        }
    };

    const projectsValidator = (_rule: unknown, value?: string) => {
        if (!value) {
            return Promise.resolve();
        }

        const existingProjectNames = store!.projects.map((p) =>
            p.name.toLowerCase(),
        );

        if (
            existingProjectNames.includes(value.toLowerCase()) &&
            store.editableProjectName?.toLowerCase() !== value.toLowerCase()
        ) {
            return Promise.reject(
                new Error("Project with the same name already exists"),
            );
        }

        if (value.length < 3) {
            return Promise.reject(
                new Error("Project name must be at least 3 characters"),
            );
        }

        return Promise.resolve();
    };

    const ownerOptions: ProjectOwnerOption[] = store.filteredUsers
        .map((user) => {
            const fullName = `${user.firstName} ${user.lastName}`.trim();
            const label = user.isDeleted ? `${fullName} (Inactive)` : fullName;

            return {
                value: user.userId,
                label,
                customFilterValue: fullName,
            };
        })
        .sort((a, b) => a.label.localeCompare(b.label));

    React.useEffect(() => {
        if (isEditing) {
            return;
        }

        let isMounted = true;

        security
            .loadUserInfo()
            .then((userInfo) => {
                const currentUserId = (userInfo as { sub?: string }).sub;

                if (
                    !isMounted ||
                    !currentUserId ||
                    form.isFieldTouched("projectOwner") ||
                    form.getFieldValue("projectOwner")
                ) {
                    return;
                }

                const currentUserExists = ownerOptions.some(
                    (option) => option.value === currentUserId,
                );

                if (currentUserExists) {
                    form.setFieldsValue({
                        projectOwner: currentUserId,
                    });
                }
            })
            .catch(() => undefined);

        return () => {
            isMounted = false;
        };
    }, [form, isEditing, ownerOptions]);

    return (
        <Form
            id="add-project-form"
            data-id="add-project-form"
            onFinish={handleSubmit}
            form={form}
            layout="vertical"
            className="create-project-form"
            initialValues={{
                name: store.editableProjectName,
                projectOwner: store.editableProjectOwner,
                enrichmentEnabled:
                    store.editableProjectEnrichmentEnabled ?? false,
            }}
        >
            <Row gutter={24}>
                <Col xs={24} md={12}>
                    <FormItem
                        label={
                            <span className="dialog-field-label required">
                                Project name
                            </span>
                        }
                        name="name"
                        rules={[
                            {
                                required: true,
                                message: "Project name is required",
                                whitespace: true,
                            },
                            { validator: projectsValidator },
                        ]}
                    >
                        <Input />
                    </FormItem>
                </Col>

                <Col xs={24} md={12}>
                    <FormItem
                        label={
                            <span className="dialog-field-label">
                                Project owner
                            </span>
                        }
                        name="projectOwner"
                        className="create-project-form__top-field"
                    >
                        <Select
                            allowClear
                            showSearch
                            placeholder="Unassigned"
                            filterOption={Utils.filterOption}
                            options={ownerOptions}
                        />
                    </FormItem>
                </Col>
            </Row>

            {isEditing ? (
                <div className="create-project-form__project-id-row">
                    <span className="create-project-form__project-id-label">
                        ID:
                    </span>
                    <span className="create-project-form__project-id-value">
                        {store.editableProjectId}
                    </span>
                    <CopyToClipboardButton
                        ariaLabel="Copy"
                        className="create-project-form__project-id-copy"
                        iconSize={11}
                        text={store.editableProjectId ?? ""}
                        title="Copy"
                    />
                </div>
            ) : null}

            <div className="create-project-form__metadata-row">
                <div className="setting-field-text">
                    <div className="setting-field-title">
                        Metadata enrichment
                    </div>
                    <div className="setting-field-description">
                        Extract metadata from incoming tasks and documents.
                    </div>
                </div>
                <FormItem
                    name="enrichmentEnabled"
                    valuePropName="checked"
                    className="create-project-form__metadata-switch"
                >
                    <Switch />
                </FormItem>
            </div>
        </Form>
    );
};

export default AddProjectForm;
